import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useUsersName, updateEventRoundId } from "../util/db";
import { Form, Button, Spinner } from "react-bootstrap";

function AdminPageEventsRow({ item, rounds, loadingRounds }) {
  // useUsersName from db to look up td name
  const {
    data: tdName,
    status: tdNameStatus,
    // error: tdNameError,
  } = useUsersName(item.td_id);

  const [tdNameDisplay, setTdNameDisplay] = useState("");
  const [roundId, setRoundId] = useState(item.round_id || "");
  const [selectedRoundId, setSelectedRoundId] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync roundId with item.round_id when component updates
  useEffect(() => {
    const currentRoundId = item.round_id ? String(item.round_id) : "";
    setRoundId(currentRoundId);
    // If no round_id, start in editing mode
    if (!item.round_id) {
      setIsEditing(true);
    }
  }, [item.round_id]);

  // Sort rounds so that those matching the event's date appear first
  const sortedRounds = useMemo(() => {
    if (!rounds || rounds.length === 0) return [];

    return [...rounds].sort((a, b) => {
      const aMatchesDate = a.round_date === item.date;
      const bMatchesDate = b.round_date === item.date;

      // If one matches the date and the other doesn't, prioritize the match
      if (aMatchesDate && !bMatchesDate) return -1;
      if (!aMatchesDate && bMatchesDate) return 1;

      // If both match or both don't match, sort by date (newest first)
      return new Date(b.round_date) - new Date(a.round_date);
    });
  }, [rounds, item.date]);

  useEffect(() => {
    if (tdNameStatus === "success") {
      setTdNameDisplay(tdName[0].name);
    }
  }, [tdNameStatus, tdName]);

  const handleDropdownChange = (e) => {
    setSelectedRoundId(e.target.value);
  };

  const handleEditClick = () => {
    setSelectedRoundId(roundId);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setSelectedRoundId("");
    setIsEditing(false);
  };

  const handleRoundIdUpdate = async () => {
    setIsSubmitting(true);

    try {
      await updateEventRoundId(item.id, selectedRoundId || null);
      // Update the actual roundId state on success
      setRoundId(selectedRoundId);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating round ID:", error);
      // Don't change editing state on error
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <tr>
      <td>
        <Link to={`/event?event=${item.id}`}>{item.name}</Link>
      </td>
      <td>
        <Link to={`/player?id=${item.td_id}`}>{tdNameDisplay}</Link>
        <Link to={`/event_ld?event=${item.id}`}>✏️</Link>
      </td>
      <td>
        <Link to={`/event_manage?event=${item.id}`}>⚙️</Link>
      </td>
      <td>{item.date}</td>
      <td>
        {item.is_event_complete ? "Complete" : "Open"}
        {!item.is_event_complete && (
          <Link to={`/event_delete?event=${item.id}`}>🗑️</Link>
        )}
      </td>
      <td>
        <div className="d-flex align-items-center gap-2">
          {loadingRounds ? (
            <span>Loading rounds...</span>
          ) : (
            <>
              {!roundId || isEditing ? (
                // Show dropdown for editing or when no round assigned
                <>
                  <Form.Control
                    as="select"
                    value={selectedRoundId}
                    onChange={handleDropdownChange}
                    disabled={isSubmitting}
                    style={{
                      width: "300px",
                      fontSize: "14px",
                      padding: "6px 12px",
                      lineHeight: "1.4",
                    }}
                  >
                    <option value="">No round selected</option>
                    {sortedRounds.map((round) => {
                      const isMatchingDate = round.round_date === item.date;
                      return (
                        <option key={round.round_id} value={round.round_id}>
                          {isMatchingDate ? "⭐ " : ""}
                          {round.round_name} - {round.round_date} (
                          {round.round_id})
                          {isMatchingDate ? " (Same Date)" : ""}
                        </option>
                      );
                    })}
                  </Form.Control>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleRoundIdUpdate}
                    disabled={isSubmitting}
                    style={{ minWidth: "80px" }}
                  >
                    {isSubmitting ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Update"
                    )}
                  </Button>
                  {roundId && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  )}
                </>
              ) : (
                // Show current round with edit button
                <>
                  <span>
                    {sortedRounds.find((r) => r.round_id === roundId)
                      ?.round_name
                      ? `${
                          sortedRounds.find((r) => r.round_id === roundId)
                            ?.round_name
                        } (${roundId})`
                      : roundId}
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleEditClick}
                    style={{ padding: "1px 4px", fontSize: "12px" }}
                  >
                    ✏️
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default AdminPageEventsRow;
